/**
 * Dashboard Analytics API Service
 *
 * Provides methods to fetch dashboard analytics data including
 * health trends, issues summary, connection heatmaps, and top failing validations.
 */

import enhancedApiService from './enhancedApiService';

const dashboardApiService = {
  /**
   * Get health score trend over time
   * @param {number} days - Number of days to look back (default: 30)
   * @param {string} connectionId - Optional connection ID filter
   * @returns {Promise<{success: boolean, dates: string[], scores: number[], current_score: number, previous_score: number, change: number, change_percent: number}>}
   */
  async getHealthTrend(days = 30, connectionId = null) {
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (connectionId) {
        params.append('connection_id', connectionId);
      }

      const response = await enhancedApiService({
        method: 'GET',
        url: `/dashboard/health-trend?${params.toString()}`
      });

      return response;
    } catch (error) {
      console.error('Error fetching health trend:', error);
      throw error;
    }
  },

  /**
   * Get issues summary with month-over-month comparison
   * @param {string} month - 'current' or 'YYYY-MM' format
   * @returns {Promise<{success: boolean, current_month: object, previous_month: object, change: number, change_percent: number}>}
   */
  async getIssuesSummary(month = 'current') {
    try {
      const response = await enhancedApiService({
        method: 'GET',
        url: `/dashboard/issues-summary?month=${month}`
      });

      return response;
    } catch (error) {
      console.error('Error fetching issues summary:', error);
      throw error;
    }
  },

  /**
   * Get connection health heatmap data
   * @returns {Promise<{success: boolean, connections: array, total_connections: number}>}
   */
  async getConnectionHeatmap() {
    try {
      const response = await enhancedApiService({
        method: 'GET',
        url: '/dashboard/connection-heatmap'
      });

      return response;
    } catch (error) {
      console.error('Error fetching connection heatmap:', error);
      throw error;
    }
  },

  /**
   * Get top failing validations
   * @param {number} limit - Number of validations to return (default: 10)
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise<{success: boolean, validations: array, total_validations: number}>}
   */
  async getTopFailingValidations(limit = 10, days = 30) {
    try {
      const response = await enhancedApiService({
        method: 'GET',
        url: `/dashboard/top-failing-validations?limit=${limit}&days=${days}`
      });

      return response;
    } catch (error) {
      console.error('Error fetching top failing validations:', error);
      throw error;
    }
  },

  /**
   * Get ROI metrics showing value delivered by Sparvi
   * @param {number} days - Number of days to look back (default: 90)
   * @returns {Promise<{success: boolean, total_cost_saved: number, issues_prevented: object, time_saved_hours: number, downtime_prevented_hours: number}>}
   */
  async getROIMetrics(days = 90) {
    try {
      const response = await enhancedApiService({
        method: 'GET',
        url: `/dashboard/roi-metrics?days=${days}`
      });

      return response;
    } catch (error) {
      console.error('Error fetching ROI metrics:', error);
      throw error;
    }
  }
};

export default dashboardApiService;
